import {
  PillsInput,
  Pill,
  useTree,
  PillsInputProps,
  Popover,
  Text,
  ActionIcon,
} from "@mantine/core"
import { getSelectedNodes } from "./getSelectedNodes"
import { TreeRenderer, TreeRendererProps } from "./index"
import { IconSelector, IconX } from "@tabler/icons-react"
import { ScrollContainer } from "../scroll-container"
import { findMaxPillCountBasedOnValueLabelLength } from "./findMaxPillCountBasedOnValueLabelLength"

export interface TreePillsInputProps<T>
  extends PillsInputProps,
    Pick<
      TreeRendererProps<T, null>,
      | "tree"
      | "initialCheckedState"
      | "initialExpandedState"
      | "onCheckedStateChange"
    > {
  // limits the maximum number of pills shown based on number of pills
  maxPills?: number
  // limits the maximum number of pills shown based on character lenght of items (+ spaces)
  maxChars?: number
}

export function TreePillsInput<T extends Record<string, any>>({
  tree,
  onCheckedStateChange,
  initialCheckedState,
  initialExpandedState,
  maxChars,
  maxPills = 5,
  ...props
}: TreePillsInputProps<T>) {
  if (maxPills !== undefined && maxChars !== undefined) {
    console.warn("if maxChars is set, maxPills does not have any effect")
  }
  const treeState = useTree({
    initialCheckedState: initialCheckedState ?? undefined,
    initialExpandedState: initialExpandedState ?? undefined,
  })

  React.useEffect(() => {
    treeState.initialize(tree)
  }, [])

  const [treeInitialised, setTreeInitialised] = React.useState(false)

  React.useEffect(() => {
    if (treeInitialised) {
      onCheckedStateChange &&
        onCheckedStateChange(
          getSelectedNodes(tree, treeState.getCheckedNodes()).map(
            (v) => v.value
          )
        )
    } else if (
      treeState.checkedState.length === (initialCheckedState ?? []).length
    ) {
      setTreeInitialised(true)
    }
  }, [treeState.checkedState])

  const values = getSelectedNodes(tree, treeState.getCheckedNodes())
  const maxPillCount =
    findMaxPillCountBasedOnValueLabelLength(maxChars, values) ?? maxPills

  return (
    <Popover disabled={props.disabled} position="bottom" withArrow shadow="md">
      <Popover.Target>
        <PillsInput
          pointer
          rightSectionPointerEvents={values?.length ? undefined : "none"}
          rightSection={
            values?.length ? (
              props.disabled ? null : (
                <ActionIcon
                  onClick={() =>
                    treeState
                      .getCheckedNodes()
                      .map((x) => treeState.uncheckNode(x.value))
                  }
                  size="xs"
                  variant="transparent"
                >
                  <IconX color="var(--mantine-color-gray-7)" size="16" />
                </ActionIcon>
              )
            ) : (
              <IconSelector
                color="var(--mantine-color-placeholder)"
                size="16"
              />
            )
          }
          styles={{ input: { alignContent: "end" } }}
          {...props}
        >
          {values?.length ? (
            <Pill.Group>
              {values
                ?.map((v) => (
                  <Pill
                    key={v.value}
                    withRemoveButton={!props.disabled}
                    onRemove={() => treeState.uncheckNode(v.value)}
                  >
                    {v.label}
                  </Pill>
                ))
                .slice(0, maxPillCount)}
              {maxPillCount !== undefined &&
              values !== undefined &&
              values.length > maxPillCount ? (
                <Pill>+{values.length - maxPillCount}</Pill>
              ) : null}
            </Pill.Group>
          ) : (
            <Text size="sm" c="var(--mantine-color-placeholder)">
              {props.placeholder}
            </Text>
          )}
        </PillsInput>
      </Popover.Target>
      <Popover.Dropdown w={props.w} maw={props.mah} p={10}>
        <ScrollContainer mah={400}>
          <TreeRenderer
            tree={tree}
            onCheckedStateChange={onCheckedStateChange}
            treeState={treeState}
          />
        </ScrollContainer>
      </Popover.Dropdown>
    </Popover>
  )
}
